import { MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CustomSearch,
  GeneralTable,
  SingleTableBoxScene,
} from "@/shared/components/ui";

import { PermissionsEnum } from "@/auth/interfaces";
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  emptyCellOneLevel,
  formatDateWithTime,
  hasPermission,
  useTableFilter,
  useTableServerSideFiltering,
} from "@/shared";

import { useDeleteResponsableV2, useFetchResponsablev2 } from "@/shared/actions/erp/responsablev2.actions";
import { Responsablev2 } from "@/shared/interfaces/erp/administration/responsablev2.interface";
import { useUiConfirmModalStore } from "@/store/ui";

export interface ResponsablePageInterface {}

const ResponsableV2Page: React.FC<ResponsablePageInterface> = () => {
  const returnResponsableUrl =
    ROUTER_PATHS.administration.responsableNav;

  const navigate = useNavigate();

  /// global state
  const setConfirmDialog = useUiConfirmModalStore(
    (s) => s.setConfirmDialog
  );
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    (s) => s.setConfirmDialogIsOpen
  );

  /// table state
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
    cleanFilter,
  } = useTableFilter();

  const { pageIndex, pageSize } = pagination;

  /// server side column filters
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  /// fetch
  const {
    data: responsablePagingRes,
    isLoading,
    isRefetching,
  } = useFetchResponsablev2({
    page: pageIndex + 1,
    page_size: pageSize,
    codigo: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  const pagingData = responsablePagingRes?.data;
  ///
  const deleteResponsableMutation = useDeleteResponsableV2();

  /// edit
  const onEdit = (original: Responsablev2) => {
    setConfirmDialog({
      isOpen: true,
      title: "Editar Responsable",
      subtitle: "¿Está seguro que desea editar este registro?",
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        navigate(
          `${returnResponsableUrl}/editar/${original.id_responsable}`
        );
      },
    });
  };
  //delete
  const onDelete = (origin:Responsablev2) =>{
    setConfirmDialog({
      isOpen: true,
      title: "Eliminar observación",
      subtitle: `¿Está seguro que desea eliminar la observación "${origin.tipo_responsable}"? Esta acción no se puede deshacer.`,
      onConfirm: () => {
        setConfirmDialogIsOpen(false);
        deleteResponsableMutation.mutate(origin.id_responsable!);
      },
    });
  };


  /// columns
  const columns = useMemo<MRT_ColumnDef<Responsablev2>[]>(
    () => [
      {
        accessorKey: "id_responsable",
        header: "ID",
        size: 200,
        Cell: ({ row }) =>
          emptyCellOneLevel(row, "id_responsable"),
      },
      {
        accessorKey: "codigo",
        header: "Código",
        size: 180,
        Cell: ({ row }) =>
          emptyCellOneLevel(row, "codigo"),
      },
      {
        accessorKey: "tipo_responsable",
        header: "Tipo Responsable",
        size: 180,
        Cell: ({ row }) =>
          emptyCellOneLevel(row, "tipo_responsable"),
      },
      {
        accessorKey: "estado",
        header: "Estado",
        size: 120,
        Cell: ({ row }) =>
          row.original?.estado ? "Activo" : "Inactivo",
      },
      {
        accessorKey: "fecha_creacion",
        header: "Fecha de Creación",
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.fecha_creacion
            ? formatDateWithTime(row.original.fecha_creacion)
            : "N/A",
      },
      {
        accessorKey: "fecha_modificacion",
        header: "Fecha de Modificación",
        size: 200,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) =>
          row.original?.fecha_modificacion
            ? formatDateWithTime(row.original.fecha_modificacion)
            : "N/A",
      },
    ],
    []
  );

  return (
    <SingleTableBoxScene
      title="Responsables"
      urlRedirect={`${returnResponsableUrl}/crear`}
      enableRedirect={hasPermission(
        PermissionsEnum.api_add_responsable
      )}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="Buscar por código"
        cleanFilter={cleanFilter}
      />

      <GeneralTable<Responsablev2>
        columns={columns}
        data={pagingData?.data || []}
        isLoading={isLoading}
        isRefetching={isRefetching}
        enableGlobalFilter={false}
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        pagination={pagination}
        onPaging={setPagination}
        rowCount={pagingData?.count}
        enableColumnActions={hasPermission(
          PermissionsEnum.api_change_responsable
        )}
        actionsColumnSize={
          TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH
        }
        positionActionsColumn="first"
        canEdit={hasPermission(
          PermissionsEnum.api_change_responsable
        )}
        onEdit={onEdit}


        canDelete={hasPermission(PermissionsEnum.api_delete_responsablev2)}
        onDelete={onDelete}
      />
    </SingleTableBoxScene>
  );
};

export default ResponsableV2Page;